// giftCardMapper — translate between the Voucher V2 FE shapes
// (voucher / sale / design from data.js) and the backend voucher API
// (catalog / instance / design). Kept FE-side so the BE mappers stay
// strictly additive and the live redemption path is untouched.

// ---- currency: the FE only carries the symbol; the BE catalog wants a code.
const SYMBOL_TO_CODE = { '£': 'GBP', '$': 'USD', '€': 'EUR' };
const codeFromSymbol = (symbol) => SYMBOL_TO_CODE[symbol] || 'GBP';

// ---- expiry <-> expirationMonths --------------------------------------------
// FE expiry is a human string ('12 months' | '24 months' | 'Never' ...);
// the BE stores expirationMonths (number | null, null = never).
export const expiryToMonths = (expiry) => {
  if (expiry === undefined || expiry === null) return undefined;
  if (typeof expiry === 'number') return expiry;
  const s = String(expiry).trim().toLowerCase();
  if (s === 'never' || s === '') return null;
  const yr = s.match(/(\d+)\s*year/);
  if (yr) return parseInt(yr[1], 10) * 12;
  const mo = s.match(/(\d+)\s*month/);
  if (mo) return parseInt(mo[1], 10);
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
};

export const monthsToExpiry = (months) => {
  if (months === null || months === undefined) return 'Never';
  const n = Number(months);
  if (isNaN(n)) return 'Never';
  return `${n} month${n === 1 ? '' : 's'}`;
};

// ---- status <-> status ------------------------------------------------------
// FE catalog status is 'active' | 'draft'; BE is 'active' | 'inactive'.
const feStatusToApi = (status) => (status === 'draft' ? 'inactive' : 'active');
const apiStatusToFe = (status) => (status === 'inactive' ? 'draft' : 'active');

// ---- voucher (product) ------------------------------------------------------
/**
 * FE voucher -> API catalog create/update body.
 * @param {Object} v - FE voucher
 * @param {string} [currencySymbol] - salon currency symbol (for currency code)
 */
export const voucherToApiCatalog = (v, currencySymbol) => {
  const body = {
    name: v.name,
    type: 'cash', // V2 gift cards are always cash value
    value: v.value,
    currency: codeFromSymbol(currencySymbol),
    status: feStatusToApi(v.status),
    channels: { pos: !!v.pos, online: !!v.online },
    expirationMonths: expiryToMonths(v.expiry),
    denominations: v.denominations || [],
    promo: v.promo !== undefined ? v.promo : null,
    promoMode: v.promoMode !== undefined ? v.promoMode : null,
    promoValue: v.promoValue !== undefined ? v.promoValue : null,
    customAmount: !!v.customAmount,
    customMin: v.customMin !== undefined ? v.customMin : null,
    customMax: v.customMax !== undefined ? v.customMax : null,
    designIds: v.designIds || [],
    // Per-voucher "title card" — the design shown to clients while they're
    // choosing this voucher (before they pick their own). One of designIds (or
    // any available design when not curating). BE: new optional field on the
    // catalog record; null = fall back to the first available design.
    coverDesignId: v.coverDesignId || null,
    tc: v.tc !== undefined ? v.tc : null,
    theme: v.theme !== undefined ? v.theme : null,
    template: v.template !== undefined ? v.template : null,
  };
  // Only treat as an update when the id is a real BE id (Mongo ObjectId).
  // FE-minted ids ('v-...') and legacy sample ids ('v1') route to create.
  if (v.id && /^[0-9a-fA-F]{24}$/.test(String(v.id))) body.catalogId = v.id;
  return body;
};

/**
 * API catalog -> FE voucher.
 */
export const apiCatalogToVoucher = (c) => {
  if (!c) return null;
  return {
    id: c.id,
    name: c.name,
    template: c.template || 'signature',
    theme: c.theme || 'rose',
    denominations: c.denominations || [],
    value: c.value,
    promo: c.promo !== undefined ? c.promo : null,
    promoMode: c.promoMode || undefined,
    promoValue: c.promoValue !== undefined ? c.promoValue : undefined,
    online: !!(c.channels && c.channels.online),
    pos: !!(c.channels && c.channels.pos),
    sold: c.sold || 0,
    status: apiStatusToFe(c.status),
    expiry: monthsToExpiry(c.expirationMonths),
    customAmount: !!c.customAmount,
    customMin: c.customMin !== undefined && c.customMin !== null ? c.customMin : undefined,
    customMax: c.customMax !== undefined && c.customMax !== null ? c.customMax : undefined,
    designIds: c.designIds || [],
    coverDesignId: c.coverDesignId || null,
    tc: c.tc || '',
  };
};

// ---- sale -------------------------------------------------------------------
/**
 * API instance -> FE sale. The BE mapper emits raw fields; we reshape into the
 * buyer/recipient split + derive redeemedAmount and the 'partial' status the
 * SalesPage helpers expect.
 */
export const apiInstanceToSale = (inst) => {
  if (!inst) return null;
  const purchaser = inst.purchaser || {};
  const purchaserDetails = inst.purchaserDetails || {};
  const recipient = inst.recipient || {};
  const faceValue = inst.faceValue || 0;
  const remainingValue = inst.remainingValue !== undefined && inst.remainingValue !== null
    ? inst.remainingValue
    : faceValue;
  const redeemedAmount = Math.max(0, faceValue - remainingValue);

  // Derive the FE status. Raw BE status is active|redeemed|expired|void.
  let status = inst.status;
  if (status === 'void') status = 'expired';
  else if (status === 'active' && redeemedAmount > 0) status = 'partial';

  return {
    id: inst.id,
    code: inst.code,
    voucherId: inst.catalogId,
    designId: inst.designId || null,
    faceValue,
    paidAmount: inst.paidAmount !== undefined && inst.paidAmount !== null ? inst.paidAmount : faceValue,
    buyerName: purchaser.name || purchaserDetails.name || null,
    buyerEmail: purchaser.email || purchaserDetails.email || null,
    recipientName: recipient.name || null,
    recipientEmail: recipient.email || null,
    message: inst.message || null,
    channel: inst.channel || 'pos',
    soldAt: inst.issuedAt,
    expiresAt: inst.expiresAt,
    redeemedAmount,
    status,
  };
};

// ---- designs ----------------------------------------------------------------
/**
 * Merge persisted design records (availability overrides + custom designs)
 * onto the static editorial/abstract base library built on the FE.
 * @param {Array} base - static designs (editorial + abstract) from data.js
 * @param {Array} persisted - API design docs ({ id, available, isCustom, config, ... })
 */
export const mergeDesigns = (base, persisted) => {
  const byId = {};
  (persisted || []).forEach((p) => { byId[p.id] = p; });

  // Apply overrides to base designs (availability + any stored fields).
  const merged = (base || []).map((d) => {
    const override = byId[d.id];
    if (!override) return d;
    delete byId[d.id];
    return {
      ...d,
      available: override.available !== undefined ? override.available : d.available,
      ...(override.config ? { config: override.config } : {}),
    };
  });

  // Append persisted designs not in the base (custom designs).
  Object.values(byId).forEach((p) => {
    merged.push({
      id: p.id,
      aesthetic: p.aesthetic || 'custom',
      theme: p.theme,
      occasion: p.occasion !== undefined ? p.occasion : null,
      label: p.label,
      available: p.available !== undefined ? p.available : true,
      isCustom: p.isCustom !== undefined ? p.isCustom : true,
      ...(p.config ? { config: p.config } : {}),
    });
  });

  return merged;
};
